import api from '../api/instance';

import {
  Page,
  PhoneNumber,
  PhoneNumberToggleRequest,
} from '@testsigmainc/models';
import { useInfiniteQuery, useQuery } from 'react-query';
import { PhoneNumberMessage } from '@models/phone-number-message.model';
import { PrivateURLConstants } from '@constants/private-url.constant';
import { logErrorToSentry } from '@utils/common';

const nameSpace = PrivateURLConstants.PHONE_NUMBERS;

const findAll = (query?, size?, page?, sort?) => {
  return api
    .get(nameSpace, {
      query,
      size,
      page,
      sort,
    })
    .then((res) => new Page<PhoneNumber>().deserialize(res, PhoneNumber))
    .catch((e) => {
      throw e;
    });
};

const show = (phoneNumberId: number) => {
  return api
    .get(nameSpace + '/' + phoneNumberId)
    .then((res) => new PhoneNumber().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const update = (phoneNumber: PhoneNumber) => {
  return api
    .put(nameSpace + '/' + phoneNumber.id, phoneNumber)
    .then((res) => new PhoneNumber().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const getLatestMessage = (phoneNumberId: number) => {
  return api
    .get(nameSpace + `/${phoneNumberId}/latest_message`)
    .then((res) => new PhoneNumberMessage().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const findAllMessages = (url: string) => {
  return api
    .get(url)
    .then((response) =>
      new Page<PhoneNumberMessage>().deserialize(response, PhoneNumberMessage),
    )
    .catch((e) => {
      throw e;
    });
};

const toggleStatus = (
  phoneNumberId: number,
  phoneNumberToggleRequest: PhoneNumberToggleRequest,
) => {
  return api
    .put(
      nameSpace + '/' + phoneNumberId + '/toggle_status',
      phoneNumberToggleRequest,
    )
    .then((res) => new PhoneNumberToggleRequest().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const PhoneNumberService = {
  findAll,
  show,
  update,
  getLatestMessage,
  findAllMessages,
  toggleStatus,
};

export default PhoneNumberService;

const useGetAllPhonenumbers = () => {
  return useQuery(
    'phonenumbers-list',
    async () => {
      const response: Page<PhoneNumber> = await api.get(nameSpace);
      return new Page<PhoneNumber>().deserialize(response, PhoneNumber);
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 10000,
    },
  );
};

export const useGetAllMessages = ({
  activeNumberId,
}: {
  activeNumberId: number;
}) =>
  useInfiniteQuery(
    'messages-infinite-query',
    async ({ pageParam = '' }) => {
      const url = pageParam
        ? `/phone_number_messages?query=phoneNumberId:${activeNumberId}&sort=id,desc&size=10&${pageParam}`
        : `/phone_number_messages?query=phoneNumberId:${activeNumberId}&sort=id,desc&page=0&size=10`;
      try {
        const response = await api.get(url);
        return new Page<PhoneNumberMessage>().deserialize(
          response,
          PhoneNumberMessage,
        );
      } catch (err) {
        logErrorToSentry({ error: err });
        throw err;
      }
    },
    {
      getPreviousPageParam: (firstPage: any) => {
        if (firstPage.number > 0 && firstPage.number - 1) {
          return `page=${firstPage.number - 1}`;
        } else return undefined;
      },
      getNextPageParam: (lastPage: any, allPages) => {
        if (!lastPage.last && lastPage.number + 1)
          return `page=${lastPage.number + 1}`;
        else return undefined;
      },
      staleTime: 10000,
    },
  );
